import { useRef, ReactNode, ChangeEvent, useState, useCallback, useEffect } from "react"
import Loader from "../../../shared/loaders/Loader"
import FileUploadIcon from "../../../../../assets/icons/react-svg/FileUploadIcon"
import NewCloseIcon from "../../../../../assets/icons/react-svg/NewCloseIcon"
import NewFillCloseIcon from "../../../../../assets/icons/react-svg/NewFillCloseIcon"
import FileUploadProgress from "./FileUploadProgress"
import styles from '../FeedbackPage.module.css'
import InfoIconSvg from "../../../../../assets/icons/react-svg/InfoIcon";
import useTooltip from "../../../../common/hooks/useTooltip";
import Tooltip from "../../../shared/tooltips/Tooltip";
import { useLanguage } from "../../../../../context/LanguageContext";

interface IFileUpload {
    label: string,
    onChange: (event: ChangeEvent<HTMLInputElement>) => void,
    isDisabled: boolean,
    isUploading?: boolean,
    children: ReactNode,
    showProgress?: boolean,
    isShowInfoIcon?: boolean,
    infoTooltipContent?: string,
    accept?: string,
    fileUploadIcon?: ReactNode,
    progressData?: {
        fileName: string,
        fileSize: string,
        progress: number
    },
    onCancelUpload?: () => void
}

const FileUpload = ({ 
    label, 
    isDisabled, 
    isUploading, 
    onChange,
    isShowInfoIcon = false,
    infoTooltipContent,
    accept = ".jpg,.jpeg,.png,.gif,.bmp,.webp,.svg",
    fileUploadIcon = <FileUploadIcon />,
    children, 
    showProgress = false, 
    progressData, 
    onCancelUpload 
}: IFileUpload) => {
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const infoIconRef = useRef<HTMLSpanElement | null>(null);
    const tooltip = useTooltip(infoIconRef, infoTooltipContent);

    const handleButtonClick = () => {
        if(fileInputRef.current) {
            fileInputRef?.current?.click();
        }
    }

    if (showProgress && progressData) {
        return (
            <FileUploadProgress
                fileName={progressData.fileName}
                fileSize={progressData.fileSize}
                progress={progressData.progress}
                onCancel={onCancelUpload || (() => {})}
            >
                {children}
            </FileUploadProgress>
        );
    }

    return (
     <>
      <input
        ref={fileInputRef} 
        type="file" 
        accept={accept}
        multiple={true} 
        onChange={onChange} 
        style={{ display: 'none' }} 
      />
       <button 
         className={`${styles.fileUploadButton} ${isDisabled && styles.disabled}`} 
         onClick={handleButtonClick}
         disabled={isDisabled}
        >
            <span className={styles.fileUploadButtonText}>
                {fileUploadIcon}&nbsp;{label}
            </span>
            {isShowInfoIcon && (
                <span
                    ref={infoIconRef}
                    className={styles.infoIconWrapper}
                >
                    <InfoIconSvg />
                    <Tooltip 
                        domRect={tooltip.domRect} 
                        text={tooltip.text} 
                        position="top" 
                        widthMax="250px"
                        adjust={{ top: 0, left: 0 }}
                    />
                </span>
            )}   
            {isUploading && <Loader className={styles.circularLoader} /> }
        </button>
       {children && <div className={styles.previewItemsContainer}>{children}</div> }
     </>
    )
}

interface IFilePreviewItem {
    label: string,
    onRemove: () => void 
}

const FilePreviewItem = ({ label, onRemove }: IFilePreviewItem) => {
    const { t } = useLanguage();
    const removeIconRef = useRef<HTMLSpanElement | null>(null);
    const removeTooltip = useTooltip(removeIconRef, t("remove"));

    return (
    <span className={styles.previewItem}>
        {label}&nbsp;
        <span
            ref={removeIconRef}
            className={styles.previewItemRemove}
            onClick={onRemove}
        >
            <span className={styles.previewItemRemoveDefault}>
                <NewCloseIcon />
            </span>
            <span className={styles.previewItemRemoveHover}>
                <NewFillCloseIcon />
            </span>
            <Tooltip
                domRect={removeTooltip.domRect}
                text={removeTooltip.text}
                position="top"
                widthMax="200px"
                adjust={{ top: -4, left: 0 }}
            />
        </span>
    </span>
    )
}

export { FileUpload, FilePreviewItem }